import React, { useMemo, useState } from "react";
import { usePacks } from "@/hooks/usePacks";
import { useBilling } from "@/hooks/useBilling";
import {
  Package,
  ShoppingCart,
  Loader2,
  Briefcase,
  FileText,
  Video,
  Search,
  Brain,
  MessageSquare,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const FEATURE_ICONS: Record<string, React.ElementType> = {
  active_jobs: Briefcase,
  byor_uploads: FileText,
  ai_interviews: Video,

  candidate_searches: Search,
  ai_candidate_ranking: Brain,
  applied_candidates_search: Search,
  ai_question_generation: MessageSquare,
  ai_jd_generation: Sparkles,
  job_applications: Briefcase,
};

export const FeaturePacksGrid: React.FC = () => {
  const { packs, loading, error } = usePacks();
  const { purchasePack, isPurchasingPack } = useBilling();
  const [purchasingId, setPurchasingId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const handleBuyPack = async (packId: string) => {
    try {
      setPurchasingId(packId);
      await purchasePack(packId);
    } catch (err) {
      console.error("Failed to purchase pack:", err);
    } finally {
      setPurchasingId(null);
    }
  };

  const grouped = useMemo(() => {
    const map = new Map<string, { featureName: string; featureKey: string; items: typeof packs }>();
    for (const p of packs) {
      const entry = map.get(p.featureKey) || {
        featureName: p.featureName,
        featureKey: p.featureKey,
        items: [] as typeof packs,
      };
      entry.items = [...entry.items, p];
      map.set(p.featureKey, entry);
    }
    return Array.from(map.values());
  }, [packs]);

  // Keep the initial pricing page dense; expansion is explicit.
  const visibleGroups = showAll ? grouped : grouped.slice(0, 4);
  const hasMore = grouped.length > 4;

  if (loading) {
    return (
      <section className="bg-[#faf9f4] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="mx-auto mb-6 h-6 w-48 border bg-[#f8f3e8]"></div>
            <div className="grid grid-cols-2 gap-px border bg-[#e7dcc4] md:grid-cols-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-32 bg-white"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="bg-[#faf9f4] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600">Unable to load feature packs.</p>
        </div>
      </section>
    );
  }

  if (grouped.length === 0) return null;

  return (
    <section className="bg-[#faf9f4] py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="mb-3 inline-flex items-center gap-2 border border-[#e7dcc4] bg-[#f8f3e8] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-[#7b5804]">
            <Package className="w-4 h-4" />
            Top-Up Packs
          </div>
          <h2 className="font-display text-3xl font-bold text-slate-900 mb-2 md:text-4xl">
            Need more? <span className="text-[#7b5804]">Buy packs</span>
          </h2>
          <p className="text-base max-w-xl mx-auto text-[#4e4637]">
            Top up specific features without changing your plan. One-time purchases, use anytime.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-px border bg-[#e7dcc4] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visibleGroups.flatMap((group) =>
            group.items.map((p) => {
              const IconComponent = FEATURE_ICONS[group.featureKey] || Package;
              const isProcessing = isPurchasingPack && purchasingId === p.id;

              return (
                <div
                  key={p.id}
                  className="group relative overflow-hidden bg-white transition-colors duration-150 hover:bg-[#f8f3e8]"
                >
                  {p.badge && (
                    <div className="absolute top-3 right-3">
                      <span className="border border-[#d4a853] bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800">
                        {p.badge}
                      </span>
                    </div>
                  )}

                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="border border-[#e7dcc4] bg-[#f8f3e8] p-1.5 transition-colors group-hover:bg-[#f2e5c7]">
                        <IconComponent className="w-4 h-4 text-[#7b5804]" />
                      </div>
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        {group.featureName}
                      </span>
                    </div>

                    <h4 className="font-semibold text-slate-900 text-sm leading-snug mb-1">
                      {p.name}
                    </h4>
                    {p.description && (
                      <p className="text-xs text-slate-500 mb-3 line-clamp-2">{p.description}</p>
                    )}

                    <div className="flex items-end justify-between mt-auto pt-2 border-t border-slate-100">
                      <div>
                        <span className="text-2xl font-bold text-slate-900">{p.price}</span>
                        <span className="text-xs text-slate-500 ml-1">/ {p.quantity} credits</span>
                      </div>
                      <button
                        onClick={() => handleBuyPack(p.id)}
                        disabled={isProcessing}
                        className="inline-flex items-center gap-1.5 border border-[#1b1c19] bg-[#1b1c19] px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#7b5804] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isProcessing ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span className="sr-only">Processing</span>
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="w-3.5 h-3.5" />
                            Buy
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {hasMore && (
          <div className="text-center mt-6">
            <button
              onClick={() => setShowAll(!showAll)}
              className="inline-flex items-center gap-2 border border-[#d7ccb8] bg-white px-5 py-2.5 text-sm font-semibold text-[#4e4637] transition-colors duration-150 hover:border-[#d4a853] hover:bg-[#f8f3e8]"
            >
              {showAll ? (
                <>
                  Show fewer packs
                  <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  Show all {grouped.length} categories
                  <ChevronDown className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturePacksGrid;
